import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import inventoryApi from '../../../services/inventoryApi';
import '../inventory.css';
import './ProductForm.css';

const ProductCategories = ({ organization }) => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedParent, setSelectedParent] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentId: null,
    code: '',
    isActive: true
  });

  useEffect(() => {
    if (organization) {
      loadCategories();
    }
  }, [organization]);

  const loadCategories = async () => {
    if (!organization) {
      setError(t('inventory.no_org_categories_error'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log('Fetching categories for organization:', organization);
      const response = await inventoryApi.getProductCategories(organization);
      
      // Handle both array and {data: []} responses
      const categoriesData = Array.isArray(response) ? response : (response.data || []);
      
      // Handle both snake_case and camelCase
      const normalizedCategories = categoriesData.map(cat => ({
        id: cat.id,
        name: cat.name || '',
        description: cat.description || '',
        parentId: cat.parent_id !== undefined ? cat.parent_id : cat.parentId,
        code: cat.code || '',
        isActive: cat.is_active !== undefined ? cat.is_active : cat.isActive !== undefined ? cat.isActive : true,
        createdAt: cat.created_at || cat.createdAt,
        updatedAt: cat.updated_at || cat.updatedAt
      }));
      
      setCategories(normalizedCategories);
      
      // Auto-expand root categories
      const rootCategories = normalizedCategories.filter(cat => !cat.parentId);
      setExpandedNodes(new Set(rootCategories.map(cat => cat.id)));
    } catch (err) {
      console.error('Error loading categories:', err);
      setError('Failed to load categories: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildTree = (parentId = null) => {
    return categories
      .filter(cat => cat.parentId === parentId)
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const toggleNode = (categoryId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      // Convert to snake_case for backend
      const categoryData = {
        name: formData.name,
        description: formData.description,
        parent_id: formData.parentId,
        code: formData.code,
        is_active: formData.isActive,
        organization: organization
      };

      if (editingCategory) {
        await inventoryApi.updateProductCategory(editingCategory.id, categoryData);
      } else {
        await inventoryApi.createProductCategory(categoryData);
      }

      // Reload categories
      await loadCategories();
      
      // Close modal and reset form
      setShowModal(false);
      setEditingCategory(null);
      setSelectedParent(null);
      resetForm();
    } catch (err) {
      console.error('Error saving category:', err);
      setError('Failed to save category: ' + err.message);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name || '',
      description: category.description || '',
      parentId: category.parentId,
      code: category.code || '',
      isActive: category.isActive !== undefined ? category.isActive : true
    });
    setSelectedParent(category.parentId);
    setShowModal(true);
  };

  const handleDelete = async (categoryId) => {
    // Check if category has children
    const hasChildren = categories.some(cat => cat.parentId === categoryId);
    if (hasChildren) {
      setError(t('inventory.cannot_delete_with_subcategories'));
      return;
    }

    if (!window.confirm(t('inventory.delete_category_confirm'))) {
      return;
    }

    try {
      await inventoryApi.deleteProductCategory(categoryId);
      await loadCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category: ' + err.message);
    }
  };

  const handleAddChild = (parentCategory) => {
    setEditingCategory(null);
    setSelectedParent(parentCategory.id);
    setFormData({
      name: '',
      description: '',
      parentId: parentCategory.id,
      code: '',
      isActive: true
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      parentId: null,
      code: '',
      isActive: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getCategoryPath = (categoryId) => {
    const path = [];
    let current = categories.find(c => c.id === categoryId);
    
    while (current) {
      path.unshift(current.name);
      current = categories.find(c => c.id === current.parentId);
    }
    
    return path.join(' > ');
  };

  const getChildrenCount = (categoryId) => {
    return categories.filter(cat => cat.parentId === categoryId).length;
  };

  const renderTreeNode = (category, level = 0) => {
    const children = buildTree(category.id);
    const hasChildren = children.length > 0;
    const isExpanded = expandedNodes.has(category.id);

    return (
      <div key={category.id} className="pm-tree-node">
        <div 
          className={`pm-tree-node-content ${!category.isActive && !category.is_active ? 'inactive' : ''}`}
          style={{ paddingLeft: `${level * 1.5}rem` }}
        >
          <div className="pm-tree-node-left">
            {hasChildren ? (
              <button
                className="pm-tree-expand-btn"
                onClick={() => toggleNode(category.id)}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            ) : (
              <span className="pm-tree-no-expand">•</span>
            )}
            
            <div className="pm-tree-info">
              <div className="pm-tree-name">
                {category.name}
                {category.code && <span className="pm-tree-code">[{category.code}]</span>}
                {hasChildren && (
                  <span className="pm-tree-count">({children.length})</span>
                )}
              </div>
              {category.description && (
                <div className="pm-tree-description">{category.description}</div>
              )}
            </div>
          </div>

          <div className="pm-tree-actions">
            {!(category.isActive || category.is_active) && (
              <span className="pm-badge-inactive">{t('inventory.active_label')}</span>
            )}
            <button
              className="pm-row-btn edit"
              onClick={() => handleAddChild(category)}
              title={t('inventory.add_subcategory_title')}
            >
              <span aria-hidden="true">➕</span>
            </button>
            <button
              className="pm-row-btn edit"
              onClick={() => handleEdit(category)}
              title={t('inventory.edit_tooltip')}
            >
              <span aria-hidden="true">✏️</span>
            </button>
            <button
              className="pm-row-btn delete"
              onClick={() => handleDelete(category.id)}
              title={t('inventory.delete_tooltip')}
            >
              <span aria-hidden="true">🗑️</span>
            </button>
          </div>
        </div>

        {hasChildren && isExpanded && (
          <div className="pm-tree-children">
            {children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pm-page-card pm-skeleton" aria-busy="true">
        {t('inventory.loading_categories')}
      </div>
    );
  }

  const rootCategories = buildTree(null);

  return (
    <div className="pm-page-card">
      <div className="pm-header">
        <h2 className="pm-header-title">{t('inventory.categories_title')}</h2>
        <div className="pm-header-actions">
          <button
            className="pm-btn-secondary"
            onClick={() => {
              const allIds = new Set(categories.map(c => c.id));
              setExpandedNodes(allIds);
            }}
          >
            <span aria-hidden="true">📂</span>
            <span className="desktop-only">{t('inventory.expand_all_button')}</span>
          </button>
          <button
            className="pm-btn-secondary"
            onClick={() => setExpandedNodes(new Set())}
          >
            <span aria-hidden="true">📁</span>
            <span className="desktop-only">{t('inventory.collapse_all_button')}</span>
          </button>
          <button 
            className="pm-btn-primary"
            onClick={() => {
              setEditingCategory(null);
              setSelectedParent(null);
              resetForm();
              setShowModal(true);
            }}
          >
            <span aria-hidden="true">➕</span>
            <span>{t('inventory.add_category_button')}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="pm-error-banner">
          <span aria-hidden="true">⚠️</span>
          <span>{error}</span>
          <button className="pm-modal-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="pm-toolbar">
        <div className="pm-stat-row">
          <div className="pm-stat-item">
            <span className="pm-stat-label">{t('inventory.total_categories_label')}</span>
            <span className="pm-stat-value">{categories.length}</span>
          </div>
          <div className="pm-stat-item">
            <span className="pm-stat-label">{t('inventory.root_categories_label')}</span>
            <span className="pm-stat-value">{rootCategories.length}</span>
          </div>
          <div className="pm-stat-item">
            <span className="pm-stat-label">{t('inventory.active_label')}</span>
            <span className="pm-stat-value">
              {categories.filter(c => c.isActive || c.is_active).length}
            </span>
          </div>
        </div>
      </div>

      <div className="pm-tree-wrap">
        {rootCategories.length === 0 ? (
          <div className="pm-empty-state">
            <div className="pm-empty-state-icon" aria-hidden="true">🗂️</div>
            <h3>{t('inventory.no_categories_found')}</h3>
            <button 
              className="pm-btn-primary"
              onClick={() => {
                setEditingCategory(null);
                setSelectedParent(null);
                resetForm();
                setShowModal(true);
              }}
            >
              <span aria-hidden="true">➕</span>
              {t('inventory.add_category_button')}
            </button>
          </div>
        ) : (
          rootCategories.map(category => renderTreeNode(category))
        )}
      </div>

      {showModal && (
        <div className="pm-modal-overlay">
          <div className="pm-modal-content">
            <div className="pm-modal-header">
              <h2>
                {editingCategory 
                  ? t('inventory.edit_category_title')
                  : selectedParent 
                    ? t('inventory.add_subcategory_title')
                    : t('inventory.add_root_category_title')}
              </h2>
              <button className="pm-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="pm-modal-body">
                {selectedParent && !editingCategory && (
                  <div className="pm-form-group full-width">
                    <label>{t('inventory.parent_category_label')}</label>
                    <div className="pm-parent-path">{getCategoryPath(selectedParent)}</div>
                  </div>
                )}

                <div className="pm-form-grid">
                  <div className="pm-form-group">
                    <label htmlFor="cat-name">
                      {t('inventory.category_name_label')}
                      <span className="pm-required"> *</span>
                    </label>
                    <input
                      id="cat-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder={t('inventory.category_name_placeholder')}
                    />
                  </div>

                  <div className="pm-form-group">
                    <label htmlFor="cat-code">{t('inventory.category_code_label')}</label>
                    <input
                      id="cat-code"
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      placeholder={t('inventory.category_code_placeholder')}
                    />
                  </div>

                  <div className="pm-form-group full-width">
                    <label htmlFor="cat-desc">{t('inventory.description_label')}</label>
                    <textarea
                      id="cat-desc"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder={t('inventory.category_description_placeholder')}
                    />
                  </div>

                  {editingCategory && (
                    <div className="pm-form-group full-width">
                      <label htmlFor="cat-parent">{t('inventory.parent_category_select')}</label>
                      <select
                        id="cat-parent"
                        name="parentId"
                        value={formData.parentId || ''}
                        onChange={(e) => {
                          const value = e.target.value === '' ? null : parseInt(e.target.value);
                          setFormData(prev => ({ ...prev, parentId: value }));
                        }}
                      >
                        <option value="">{t('inventory.root_category_option')}</option>
                        {categories
                          .filter(cat => cat.id !== editingCategory.id) // Can't be its own parent
                          .map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {getCategoryPath(cat.id)}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}

                  <div className="pm-toggle-row">
                    <label htmlFor="cat-active">
                      <input
                        id="cat-active"
                        type="checkbox"
                        name="isActive"
                        role="switch"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      {t('inventory.active_checkbox')}
                    </label>
                  </div>
                </div>
              </div>

              <div className="pm-modal-footer">
                <button
                  type="button"
                  className="pm-btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  {t('inventory.cancel_button')}
                </button>
                <button type="submit" className="pm-btn-primary">
                  {editingCategory ? t('inventory.update_category_button') : t('inventory.create_category_button')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCategories;
